import useJWT from 'hooks/useJWT';
import React, {
  ReactElement, ReactNode, useContext, useMemo,
} from 'react';
import * as models from 'models/jwt';
import * as authentication from 'models/authentication';

type ContextValue = {
  jwt: authentication.LoginResult | undefined;
  setJwt: (jwt?: authentication.LoginResult, remember?: boolean) => void;
};

/**
 * The context for holding the user's JWT bearer token.
 */
export const JWTContext = React.createContext<ContextValue>({
  jwt: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setJwt: () => {},
});

/**
 * The provider for a JWTContext.
 * @param param0 Properties are only child ReactNodes.
 * @returns The children wrapped in a JWTContext provider.
 */
export const JWTProvider = ({ children } : {children: ReactNode}): ReactElement => {
  const [, setJwt, tokens] = useJWT();

  const contextValue = useMemo(() => ({ setJwt, jwt: tokens }), [setJwt, tokens]);
  console.log(tokens);

  return (
    <JWTContext.Provider value={contextValue}>
      {children}
    </JWTContext.Provider>
  );
};

type ReturnType = [
  authentication.LoginResult | undefined,
   (jwt: authentication.LoginResult | undefined, remember?: boolean) => void
  ];

/**
 * A hook that through a JWTContext provides access to a user's JWT.
 * @returns The user's JWT and a function to update it.
 */
export const useJWTokens = (): ReturnType => {
  const { jwt, setJwt } = useContext(JWTContext);

  return useMemo(() => [jwt, setJwt], [jwt, setJwt]);
};

/**
 * A hook that through a JWTContext provides access the identity stored in the user's JWT.
 * @returns The user identity held in JWT.
 */
export const useJWTUser = (): models.identity.IJWTIdentity | undefined => {
  const [jwt] = useJWTokens();

  return useMemo(() => (jwt ? models.identity.Parse(jwt.token) : undefined), [jwt]);
};
